import {
  EMAIL_VALIDATION_MESSAGE,
  MAX_CHARACTER_MESSAGE,
  REQUIRED_MESSAGE,
} from "@/utils/constant";
import { axiosInstance } from "@/utils/lib/axios";
import {
  QueryClient,
  QueryFunctionContext,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { LoaderFunction, LoaderFunctionArgs } from "react-router-dom";
import { z } from "zod";
import { Paginated } from "./types";
import { generateParams } from "@/utils/helper/generateParams";
import { DataConfig } from "@/components/table/useDefaultDataConfig";
import { stringRange } from "@/utils/helper/refinement";

const organizationPayloadSchema = z.object({
  companyCode: z
    .string()
    .min(1, { message: REQUIRED_MESSAGE })
    .refine(...stringRange(3, 8)),
  companyName: z
    .string()
    .min(1, { message: REQUIRED_MESSAGE })
    .refine(...stringRange(4, 50)),
  department: z
    .string()
    .min(1, { message: REQUIRED_MESSAGE })
    .refine(...stringRange(15, 20)),
  noTelp: z
    .string()
    .min(1, { message: REQUIRED_MESSAGE })
    .refine(...stringRange(11, 13)),
  email: z
    .string()
    .min(1, { message: REQUIRED_MESSAGE })
    .email({ message: EMAIL_VALIDATION_MESSAGE })
    .max(255, { message: MAX_CHARACTER_MESSAGE }),
  address: z
    .string()
    .min(1, { message: REQUIRED_MESSAGE })
    .refine(...stringRange(4, 250)),
});

type OrganizationPayloadSchema = z.infer<typeof organizationPayloadSchema>;

type OrganizationData = {
  id: string;
  companyCode: string;
  companyName: string;
  department: string;
  noTelp: string;
  email: string;
  address: string;
};

/* ============= *
 * READ organization *
 * ============= */

type OrganizationsQueryKey = DataConfig & { organizationName?: string };

interface OrganizationList extends Paginated {
  content: OrganizationData[];
}
async function getOrganizations(
  queryFunctionContext: QueryFunctionContext,
): Promise<OrganizationList> {
  const { queryKey } = queryFunctionContext;
  const [_, organizationsQueryKey] = queryKey;
  const params = generateParams(organizationsQueryKey as OrganizationsQueryKey);

  const response = await axiosInstance.get("/api/crud/organization" + params);
  return response.data.data;
}

function useOrganizations(
  organizationsQueryKey: OrganizationsQueryKey = {},
  options?: {
    enabled?: boolean;
  },
) {
  return useQuery({
    ...options,
    queryKey: ["organizations", organizationsQueryKey],
    queryFn: getOrganizations,
  });
}

interface OrganizationQueryKey {
  id: string;
}

async function getOrganization(
  queryFunctionContext: QueryFunctionContext,
): Promise<OrganizationData> {
  const { queryKey } = queryFunctionContext;
  const [_, organizationQueryKey] = queryKey;
  const { id } = organizationQueryKey as OrganizationQueryKey;

  const response = await axiosInstance.get(`/api/crud/organization/${id}`);
  return response.data.data;
}

function useOrganization(organizationQueryKey: OrganizationQueryKey) {
  const { id } = organizationQueryKey;
  return useQuery({
    queryKey: ["organization", { id }],
    queryFn: getOrganization,
  });
}

function organizationLoader(
  queryClient: QueryClient,
): LoaderFunction<OrganizationData> {
  return async ({ params }: LoaderFunctionArgs<{ id: string }>) => {
    const organization = await queryClient.ensureQueryData({
      queryKey: ["organization", { id: params.id }],
      queryFn: getOrganization,
    });
    return organization;
  };
}

/* =============== *
 * CREATE organization *
 * =============== */

async function createOrganization(
  data: OrganizationPayloadSchema,
): Promise<OrganizationData> {
  const response = await axiosInstance.post("/api/crud/organization", data);
  return response.data;
}

function useOrganizationCreate() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ data }: { data: OrganizationPayloadSchema }) =>
      createOrganization(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["organizations"] });
    },
  });
}

/* =============== *
 * UPDATE organization *
 * =============== */

async function editOrganization(id: string, data: OrganizationPayloadSchema) {
  const response = await axiosInstance.post(
    `/api/crud/organization/${id}`,
    data,
  );
  return response.data;
}

function useOrganizationEdit() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      data,
    }: {
      id: string;
      data: OrganizationPayloadSchema;
    }) => editOrganization(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["organizations"] });
    },
  });
}

/* =============== *
 * DELETE organization *
 * =============== */

async function deleteOrganization(id: string) {
  const response = await axiosInstance.delete(`/api/crud/organization/${id}`);
  return response.data;
}

function useOrganizationDelete() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id }: { id: string }) => deleteOrganization(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["organizations"] });
    },
  });
}

async function deleteBulkOrganization(ids: string[]) {
  return await Promise.all(ids.map((id) => deleteOrganization(id)));
}

function useOrganizationBulkDelete() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ ids }: { ids: string[] }) => deleteBulkOrganization(ids),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["organizations"] });
    },
  });
}

/* =============== *
 * DOWNLOAD organization *
 * =============== */

async function waitForMediaCompletion(
  id: string,
  interval = 2000,
): Promise<{ status: string; path?: string; fileName?: string }> {
  let currentStatus = "";
  let path: string | undefined;
  let fileName: string | undefined;
  while (currentStatus !== "SUCCESS") {
    const res = await axiosInstance.get(`/api/crud/media/${id}`);
    currentStatus = res.data.data.status;
    path = res.data.data.path;
    fileName = res.data.data.originalFileName ?? "list-apd.xlsx";

    if (currentStatus === "ERROR") {
      return { status: "ERROR", path, fileName };
    }

    if (currentStatus !== "SUCCESS") {
      await new Promise((resolve) => setTimeout(resolve, interval));
    }
  }
  return { status: currentStatus, path, fileName };
}

async function exportOrganization(
  config: DataConfig = {},
): Promise<{ status: string; fileName?: string }> {
  const configCopy = { ...config };

  // Step 1: Create Excel
  const createResponse = await axiosInstance.get(
    "/api/crud/organization/excel",
    { params: configCopy },
  );

  // Step 2: Monitor Status
  const statusResponse = await waitForMediaCompletion(
    createResponse.data.data.id,
  );

  if (statusResponse.status === "SUCCESS") {
    // Step 3: Download File
    const downloadResponse = await axiosInstance.get(
      "/api/crud/media/download",
      {
        params: {
          path: statusResponse.path,
          fileName: statusResponse.fileName,
        },
        responseType: "blob",
      },
    );

    const url = window.URL.createObjectURL(new Blob([downloadResponse.data]));
    const link = document.createElement("a");
    link.href = url;
    const filename: string | undefined = statusResponse.fileName;
    link.setAttribute("download", filename ?? "organization-list.xlsx");
    document.body.appendChild(link);
    link.click();
    link.remove();
  }

  // Return the final status and file name
  return { status: statusResponse.status };
}
function useExportOrganization() {
  return useMutation({
    mutationFn: async ({ config }: { config?: DataConfig; name?: string }) => {
      return await exportOrganization(config);
    },
  });
}

export type { OrganizationData, OrganizationPayloadSchema };
export {
  organizationPayloadSchema,
  organizationLoader,
  useOrganizations,
  useOrganization,
  useOrganizationCreate,
  useOrganizationEdit,
  useOrganizationDelete,
  useOrganizationBulkDelete,
  useExportOrganization,
  // Exporting for testing purposes
  exportOrganization,
  waitForMediaCompletion,
};
